import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button, toast, Notification, Spinner } from "components/ui";
import PlainInput from "views/v2/shared/PlainInput";
import Input from "views/v2/shared/Input";
import AccountService from "views/v2/services/accountService";
import { ImSpinner9 } from "react-icons/im";
import PasswordInput from "views/v2/shared/PasswordInput";

const IntegrationForm = ({ integrationId, onClose, onSaveSuccess }) => {
  const {
    control,
    reset,
    getValues,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      url: "",
      port: "",
      isSecure: true,
      username: "",
      password: "",
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const isViewMode = Boolean(integrationId);
  const hasUnsavedChanges = Object.keys(dirtyFields).length > 0;

  const fetchIntegrations = async () => {
    if (!integrationId) return;

    setLoading(true);
    try {
      const response = await AccountService.getEmailIntegrationsById(
        integrationId
      );
      const integration = response.data.data;

      reset({
        url: integration.info.url || "",
        port: integration.info.port || "",
        isSecure: integration.info.isSecure ?? true,
        username: integration.info.username || "",
        password: integration.info.password || "",
      });
    } catch (error) {
      console.error("Failed to fetch integration:", error);
      toast.push(
        <Notification title="Error" type="danger" closable>
          Failed to load SMTP configuration.
        </Notification>
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (integrationId) fetchIntegrations();
  }, [integrationId]);

  // Save handler
  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    setIsSubmitting(true);
    setLoading(true);

    try {
      const values = getValues();

      const payload = {
        name: "SMTP",
        provider: "SMTP",
        type: "EMAIL",
        config: {
          url: values.url.trim(),
          port: Number(values.port),
          isSecure: values.isSecure,
          username: values.username.trim(),
          password: values.password,
        },
      };

      await AccountService.smtpIntegration(payload);
      onSaveSuccess();

      toast.push(
        <Notification title="Success" type="success" closable>
          SMTP settings saved successfully!
        </Notification>
      );

      onClose();
    } catch (error) {
      console.error(error);
      toast.push(
        <Notification title="Error" type="danger" closable>
          Failed to save SMTP settings.
        </Notification>
      );
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const onTest = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    setTesting(true);

    try {
      const values = getValues();

      const payload = {
        url: values.url.trim(),
        port: Number(values.port),
        isSecure: values.isSecure,
        username: values.username.trim(),
        password: values.password,
      };

      await AccountService.testSMTP(payload);

      toast.push(
        <Notification title="Success" type="success" closable>
          Test Connection Successful!
        </Notification>
      );
    } catch (error) {
      console.error(error);
      toast.push(
        <Notification title="Error" type="danger" closable>
          Test Connection Failed
        </Notification>
      );
    } finally {
      setTesting(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to cancel?"
        )
      )
        return;
    }
    onClose();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={40} indicator={ImSpinner9} />
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4 
  px-4 py-4 
  sm:px-6 md:px-8 lg:px-10 
   border border-gray-700"
    >
      <h3 className="text-black dark:text-white font-semibold mb-4 text-center w-full">
        SMTP Configuration
      </h3>

      <div className="grid grid-cols-1 gap-4 text-[11px]">
        {/* URL */}
        <div className="flex flex-col w-full">
          <label className="form-label mb-1">
            SMTP Server Address <span className="text-red-500">*</span>
          </label>
          <Controller
            name="url"
            control={control}
            rules={{
              required: "SMTP URL is required",
              pattern: {
                value: /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message:
                  "Please enter a valid SMTP host (e.g., smtp.gmail.com)",
              },
            }}
            render={({ field }) => (
              <PlainInput
                {...field}
                placeholder="SMTP server address"
                className="normal-case"
                error={errors.url}
                disabled={isSubmitting || isViewMode}
              />
            )}
          />
          {errors.url && (
            <p className="text-red-500 text-sm mt-1">{errors.url.message}</p>
          )}
        </div>

        {/* PORT */}
        <div className="flex flex-col w-full">
          <label className="form-label mb-1">
            Port <span className="text-red-500">*</span>
          </label>
          <Controller
            name="port"
            control={control}
            rules={{
              required: "Port is required",
              validate: (value) =>
                !isNaN(Number(value)) || "Port must be a number",
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                placeholder="587"
                error={errors.port}
                maxLength={4}
                disabled={isSubmitting || isViewMode}
              />
            )}
          />
          {errors.port && (
            <p className="text-red-500 text-sm mt-1">{errors.port.message}</p>
          )}
        </div>

        {/* IS SECURE */}
        <div className="flex flex-col w-full">
          <label className="form-label mb-1">
            Is Secure <span className="text-red-500">*</span>
          </label>

          {isViewMode ? (
            <p className="text-black dark:text-white font-medium">
              {getValues("isSecure") ? "Yes" : "No"}
            </p>
          ) : (
            <Controller
              name="isSecure"
              control={control}
              render={({ field }) => (
                <div className="flex gap-4">
                  {[true, false].map((val) => (
                    <label
                      key={val.toString()}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        checked={field.value === val}
                        onChange={() => field.onChange(val)}
                        className="form-radio accent-green-600"
                      />
                      <span className="ml-2 text-gray-200">
                        {val ? "Yes" : "No"}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            />
          )}
        </div>

        {/* USERNAME */}
        <div className="flex flex-col w-full">
          <label className="form-label mb-1">
            Username <span className="text-red-500">*</span>
          </label>
          <Controller
            name="username"
            control={control}
            rules={{
              required: "Username is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address",
              },
            }}
            render={({ field }) => (
              <PlainInput
                {...field}
                type="email"
                placeholder="Username"
                error={errors.username}
                disabled={isSubmitting || isViewMode}
              />
            )}
          />
          {errors.username && (
            <p className="text-red-500 text-sm mt-1">
              {errors.username.message}
            </p>
          )}
        </div>

        {/* PASSWORD (only when creating) */}
        {!integrationId && (
          <div className="mb-6">
            <label className="form-label mb-2">
              Password<span className="text-red-500">*</span>
            </label>
            <Controller
              name="password"
              control={control}
              rules={{ required: "Please enter the password" }}
              render={({ field }) => (
                <PasswordInput {...field} placeholder="Password" />
              )}
            />
            {errors.password && (
              <p className="text-red-500 text-left">
                {errors.password.message}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ACTION BUTTONS */}
      {!isViewMode && (
        <div
          className="flex justify-end gap-3 
      px-4 py-3 
      sm:px-6 md:px-8 lg:px-10"
        >
          <Button
            type="button"
            variant="solid"
            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="solid"
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </form>
  );
};

export default IntegrationForm;
